import camelize from 'camelize';
import snakeize from 'snakeize';

import { t } from 'i18next';

import { AD_STATUSES, PLATFORMS } from 'app/utils/constants';
import { FirebaseDispatcher } from 'app/utils/firebase';

import { setValue } from 'app/redux/common/actions';

const { APP_ENV } = process.env;
const __PROD__ = APP_ENV === 'production';

const toJSONString = (obj) => JSON.stringify(snakeize(obj));

/*
  web -> app
*/
class WebDispatcher {
  constructor(store, interface_) {
    this.store = store;
    this.interface = interface_;

    const { platform, version } = this.store.getState().common;

    this.platform = platform;
    this.version = version;
  }

  _dispatch = (command, androidData = [], iosData = {}) => {
    if (window.ChatieAndroidInterface) {
      if (!__PROD__) {
        console.log('interface', command, androidData);
      }

      if (typeof window.ChatieAndroidInterface[command] !== 'undefined') {
        window.ChatieAndroidInterface[command](...androidData);
      }
    } else if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.chatie
    ) {
      if (!__PROD__) {
        console.log('interface', command, iosData);
      }

      window.webkit.messageHandlers.chatie.postMessage({
        command,
        ...iosData,
      });
    } else if (!__PROD__) {
      console.log('interface', command, androidData, iosData);
    }
  };

  initialized() {
    this._dispatch('initialized');
  }

  startMaintenance() {
    this._dispatch('startMaintenance');
  }

  leave() {
    this._dispatch('leave');
  }

  openPhoneCertification() {
    this._dispatch('openPhoneCertification');
  }

  openPage(path) {
    const { source } = this.store.getState().common;

    let newPath = path;
    if (source) {
      if (path.includes('?')) {
        newPath = `${path}&source=${source}`;
      } else {
        newPath = `${path}?source=${source}`;
      }
    }

    this._dispatch('openPage', [newPath], { path: newPath });
  }

  openUrl(url) {
    this._dispatch('openUrl', [url], { url });
  }

  closePage() {
    this._dispatch('closePage');
  }

  select(name, options) {
    const optionsStringify = JSON.stringify(options);
    this._dispatch('select', [name, optionsStringify], {
      name,
      options: optionsStringify,
    });
  }

  transferRemoteConfigValue(key) {
    this._dispatch('transferRemoteConfigValue', [key], { key });
  }

  share(type, url, title = '', summary = '', cover = '') {
    this._dispatch('share', [type, url, title, summary, cover], {
      type,
      url,
      title,
      summary,
      cover,
    });
  }

  showAppReview() {
    this._dispatch('showAppReview');
  }

  settings() {
    this._dispatch('settings');
  }

  settingsPush() {
    this._dispatch('settingsPush');
  }

  loadAd(unitId) {
    if (!unitId) {
      return;
    }

    const appAdType = window.Ad.getAppAdType(unitId);
    this._dispatch('loadAd', [appAdType, unitId], {
      adType: appAdType,
      unitId,
    });
  }

  viewAd(unitId, meta) {
    const adType = window.Ad.getAdType(unitId);
    const status = this.store.getState().common.adLoadStatus[adType];
    if (status === AD_STATUSES.FAILED) {
      alert(
        t('준비된 광고가 없습니다. 잠시후 다시 시도해주세요.', {
          ns: 'message',
        })
      );
      const appAdType = window.Ad.getAppAdType(unitId);
      this._dispatch('loadAd', [appAdType, unitId], {
        adType: appAdType,
        unitId,
      });
      return;
    }

    this.store.dispatch(setValue('currentAdMeta', meta));
    const appAdType = window.Ad.getAppAdType(unitId);
    this._dispatch('viewAd', [appAdType, unitId], {
      adType: appAdType,
      unitId,
    });
  }

  setStatusBarColor(color = 'black') {
    if (this.platform !== PLATFORMS.IOS) {
      return;
    }

    setTimeout(() => {
      this._dispatch('setStatusBarColor', [color], { color });
    }, 1);
  }

  reloadTab(tab) {
    this._dispatch('reloadTab', [tab], { tab });
  }

  switchTab(tab) {
    this._dispatch('switchTab', [tab], { tab });
  }

  inAppPurchase(bundleId) {
    this._dispatch('inAppPurchase', [bundleId], { bundleId });
  }

  startOfferwall(rewardType = 'coin') {
    this._dispatch('startOfferwall', [rewardType], { rewardType });
  }

  readEpisode(
    storyId,
    episodeId,
    episodeCount,
    episodeTitle,
    sceneIndex,
    chatIndex
  ) {
    this._dispatch(
      'readEpisode',
      [storyId, episodeId, episodeCount, episodeTitle, sceneIndex, chatIndex],
      {
        storyId,
        episodeId,
        episodeCount,
        episodeTitle,
        sceneIndex,
        chatIndex,
      }
    );
  }

  sendMigrationData(name, jsonData) {
    this._dispatch('migrationData', [name, jsonData], { name, jsonData });
  }

  mutate(action, data) {
    const jsonData = toJSONString(data);

    this._dispatch('mutate', [action, jsonData], { action, jsonData });
  }

  openPayment(
    history,
    episode,
    story,
    isEpisodePurchaseModalShow = false,
    legacyCallback = null
  ) {
    if (this.platform === null) {
      if (legacyCallback) {
        legacyCallback();
      } else {
        const confirmContents =
          episode.isWaitfreeBlocked && !isEpisodePurchaseModalShow
            ? t(
                '최신 {{waitfreeBlockCount}} 회차는 기다리면 무료로 볼 수 없습니다.\n코인 충전소로 이동하시겠습니까?',
                { waitfreeBlockCount: story.waitfreeBlockCount }
              )
            : t('풍선이 부족합니다. 충전소로 이동하시겠습니까?');
        const r = window.confirm(confirmContents);
        if (r) {
          window.ci.web.openPage('/payment');
        } else {
          history.goBack();
        }
      }
    } else {
      const episodeId = episode.id;
      const storyId = story.id;
      this._dispatch('openPayment', [episodeId, storyId], {
        episodeId,
        storyId,
      });
    }
  }

  openPaymentResult(impUid, itemId, impSuccess, isFromEpisode) {
    this._dispatch(
      'openPaymentResult',
      [impUid, itemId, impSuccess, isFromEpisode],
      {
        impUid,
        itemId,
        impSuccess,
        isFromEpisode,
      }
    );
  }

  // ios
  showStatusBar() {
    if (this.platform === PLATFORMS.ANDROID) {
      return;
    }

    this._dispatch('showStatusBar');
  }

  // ios
  hideStatusBar() {
    if (this.platform === PLATFORMS.ANDROID) {
      return;
    }

    this._dispatch('hideStatusBar');
  }

  data(key, value) {
    if (value) {
      const valueStr = toJSONString(value);
      this._dispatch('data', [key, valueStr], { key, value: valueStr });
    } else {
      this._dispatch('data', [key], { key });
    }
  }

  enterLive(userId) {
    this._dispatch('enterLive', [userId], { userId });
  }
}

/*
  app -> web
*/
class AppDispatcher {
  constructor(store, interface_) {
    this.store = store;
    this.interface = interface_;

    const { platform, version } = this.store.getState().common;

    this.platform = platform;
    this.version = version;
  }

  /* eslint-disable */
  _receive(event, detail = {}) {
    const e = new CustomEvent(event, { detail });
    dispatchEvent(e);
  }
  /* eslint-enable */

  openedAd(_, unitId) {
    const state = this.store.getState();
    const { storyId, episodeId, storyTitle } = state.common.currentAdMeta;

    const adType = window.Ad.getAdType(unitId);

    if (!storyId || !episodeId) {
      return;
    }

    // airbridge
    window.airbridgeTriggerEvent('airbridge.adImpression', {
      action: storyId.toString(),
      ...(storyTitle ? { label: storyTitle } : {}),
      value: 3,
      customAttributes: {
        episodeId: episodeId.toString(),
      },
    });

    this.interface.firebase.logEvent('open_ads', {
      storyId,
      episodeId,
      type: adType,
      unitId,
    });
    this._receive('openedAd', { adType, unitId });
  }

  loadedAd(_, unitId) {
    const adType = window.Ad.getAdType(unitId);
    this._receive('loadedAd', { adType, unitId });
  }

  closedAd(_, unitId) {
    const adType = window.Ad.getAdType(unitId);
    const { currentAdMeta } = this.store.getState().common;
    const { storyId, episodeId } = currentAdMeta;
    if (!storyId || !episodeId) {
      return;
    }

    this.interface.firebase.logEvent('close_ads', {
      storyId,
      episodeId,
      type: adType,
      unitId,
    });
    this._receive('closedAd', { adType, unitId, meta: currentAdMeta });
  }

  rewardedAd(unitId) {
    const adType = window.Ad.getAdType(unitId);
    const { currentAdMeta } = this.store.getState().common;
    const { storyId, episodeId } = currentAdMeta;
    if (!storyId || !episodeId) {
      return;
    }

    this.interface.firebase.logEvent('reward_ads', {
      storyId,
      episodeId,
      type: adType,
      unitId,
    });
    this._receive('rewardedAd', { adType, unitId, meta: currentAdMeta });
  }

  failedToLoadAd(_, unitId, errorCode) {
    const adType = window.Ad.getAdType(unitId);
    this.interface.firebase.logEvent('failed_to_load_ads', {
      errorCode,
      type: adType,
      unitId,
    });
    this._receive('failedToLoadAd', { adType, unitId, errorCode });
  }

  transferABTestData(data) {
    this._receive('transferABTestData', { data });
  }

  transferRemoteConfigValue(key, value) {
    this._receive('transferRemoteConfigValue', { key, value });
  }

  setDeviceSetting() {}

  registerDevice() {}

  select(name, value) {
    this._receive('select', { name, value });
  }

  induceAppReview(value) {
    this._receive('induceAppReview', { value });
  }

  rerender() {
    this._receive('rerender');
  }

  paymentResult(status, orderId) {
    this._receive('paymentResult', { status, orderId });
  }

  setSessionId(sessionId) {
    this._receive('setSessionId', { sessionId });
  }

  data(key, _value) {
    const value = camelize(_value);
    this._receive('data', { key, value });
  }
}

const _addEventListener = (name, cb, useCapture = false) => {
  window.addEventListener(name, cb, useCapture);

  return () => {
    window.removeEventListener(name, cb, useCapture);
  };
};

class Interface {
  constructor(store, firebaseApp = null) {
    this.web = new WebDispatcher(store, this);
    this.app = new AppDispatcher(store, this);
    this.firebase = new FirebaseDispatcher(firebaseApp, store, this);
    this.on = _addEventListener;
  }
}

export default Interface;



// WEBPACK FOOTER //
// ./src/app/utils/interface.js